import { Input, InputProps, InputRef } from 'antd'
import cx from 'classnames'
import { FC, memo, useRef } from 'react'
import { WrappedFieldInputProps, WrappedFieldMetaProps } from 'redux-form'
import { ReactComponent as ErrorIcon } from '../../assets/icons/error.svg'
import { formFieldID } from '../../utils/helpers'

interface IIBANInputField extends Omit<InputProps, 'value' | 'onChange'> {
	label?: string
	input: Partial<WrappedFieldInputProps>
	meta: Partial<WrappedFieldMetaProps>
	inputClassName?: string
}

const formatIBAN = (raw: string): string => {
	const cleaned = raw.replace(/[^A-Z0-9]/gi, '').toUpperCase()
	return cleaned.replace(/(.{4})/g, '$1 ').trim()
}

const calcCursorPos = (formatted: string, rawCharsBeforeCursor: number): number => {
	if (rawCharsBeforeCursor === 0) return 0
	let rawCount = 0
	for (let i = 0; i < formatted.length; i++) {
		if (formatted[i] !== ' ') rawCount++
		if (rawCount === rawCharsBeforeCursor) return i + 1
	}
	return formatted.length
}

// Max IBAN length is 34 chars; with 8 spaces between groups = 42 display chars
const IBAN_MAX_LENGTH = 42

const IBANInputField: FC<IIBANInputField> = ({ label, placeholder, disabled, className, style, input, meta, inputClassName, ...props }) => {
	const { name = 'field', value = '', onChange } = input
	const { form = 'form', error = undefined, touched = false } = meta

	const id = formFieldID(form, name)
	const inputRef = useRef<InputRef>(null)

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const target = e.target
		const cursorPos = target.selectionStart ?? 0
		const inputValue = target.value

		let rawCharsBeforeCursor = 0
		for (let i = 0; i < cursorPos; i++) {
			if (inputValue[i] !== ' ') rawCharsBeforeCursor++
		}

		const formatted = formatIBAN(inputValue)
		const newCursorPos = calcCursorPos(formatted, rawCharsBeforeCursor)

		requestAnimationFrame(() => {
			const inputEl = inputRef.current?.input
			if (inputEl) inputEl.setSelectionRange(newCursorPos, newCursorPos)
		})

		if (onChange) onChange(formatted as any)
	}

	return (
		<div className={cx('form-field', className)} style={style}>
			{label && (
				<label htmlFor={id} className={'form-field-label'}>
					{label}
				</label>
			)}
			<div className={'form-field-item'}>
				<Input
					ref={inputRef}
					id={id}
					placeholder={placeholder}
					disabled={disabled}
					value={formatIBAN(String(value))}
					onChange={handleChange}
					maxLength={IBAN_MAX_LENGTH}
					spellCheck={false}
					autoCapitalize='characters'
					className={cx(inputClassName, { error: error && touched })}
					{...props}
				/>
				<span className={'form-field-item-suffix'}>{error && touched && <ErrorIcon />}</span>
			</div>
			{error && touched && <span className={'form-field-message'}>{error}</span>}
		</div>
	)
}

export default memo(IBANInputField)
